import * as React from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, FormControl, Grid, InputLabel, LinearProgress,
    Link as MuiLink, MenuItem, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton,
    ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { BarChart } from '@mui/x-charts/BarChart';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { AI_LABELS, getAiAgents, getAiToolInvocations, getAiToolStats } from '../../Api';
import {
    AiAgentSummaryDto, AiToolErrorType, AiToolInvocationDto, AiToolInvocationStatus, AiToolStatDto, AiToolStatsDto,
} from '../../models/dtos/AiDto';
import { aiLoadErrorText, formatAiCount, formatAiDate, formatAiMs, formatAiRate } from '../../components/ai/AiShared';

const PERIODS = [
    { label: '24 h', hours: 24 },
    { label: '7 days', hours: 24 * 7 },
    { label: '30 days', hours: 24 * 30 },
];

type StatusFilter = '' | AiToolInvocationStatus | `ERROR:${AiToolErrorType}`;

/** A failure rate as a colored bar: green under 5 %, orange under 20 %, red above. */
function RateBar({ rate }: { rate: number }) {
    const color = rate < 0.05 ? 'success' : rate < 0.2 ? 'warning' : 'error';
    return (
        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 120 }}>
            <LinearProgress variant="determinate" value={Math.min(100, rate * 100)} color={color} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
            <Typography variant="body2" sx={{ width: 48, textAlign: 'right' }}>{formatAiRate(rate)}</Typography>
        </Stack>
    );
}

function Metric({ label, value, hint, color }: { label: string; value: React.ReactNode; hint?: string; color?: string }) {
    const body = (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color }}>{value}</Typography>
            </CardContent>
        </Card>
    );
    return hint ? <Tooltip title={hint}>{body}</Tooltip> : body;
}

function StatusChip({ row }: { row: AiToolInvocationDto }) {
    if (row.status === 'SUCCESS') return <Chip size="small" color="success" variant="outlined" label="Success" />;
    return (
        <Tooltip title={row.errorType === 'CALL' ? 'Could not be called (unreachable, unknown tool, call limit…)' : 'The tool answered with an error'}>
            <Chip size="small" color="error" label={row.errorType === 'CALL' ? 'Call failed' : 'Tool error'} />
        </Tooltip>
    );
}

/**
 * AI › Tools: every tool call of the agents (MCP tools, task tools, subagents) from the hub's tool_invocation log —
 * per tool calls, failure rate and latency, a time series, and the invocations with their error messages.
 */
export default function AiToolsPage() {
    const [period, setPeriod] = React.useState(0);
    const [searchParams] = useSearchParams();
    const [agentCode, setAgentCode] = React.useState(searchParams.get('agentCode') ?? '');
    const [agents, setAgents] = React.useState<AiAgentSummaryDto[]>([]);
    const [selectedTool, setSelectedTool] = React.useState<AiToolStatDto | null>(null);
    const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('ERROR');
    const [stats, setStats] = React.useState<AiToolStatsDto | null>(null);
    const [statsError, setStatsError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [rows, setRows] = React.useState<AiToolInvocationDto[]>([]);
    const [total, setTotal] = React.useState(0);
    const [rowsLoading, setRowsLoading] = React.useState(false);
    const [rowsError, setRowsError] = React.useState<string | null>(null);
    const [paging, setPaging] = React.useState<GridPaginationModel>({ page: 0, pageSize: 25 });
    const [reloadKey, setReloadKey] = React.useState(0);
    const hours = PERIODS[period].hours;

    React.useEffect(() => {
        getAiAgents({}, 0, 200).then(page => setAgents(page?.items ?? []));
    }, []);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getAiToolStats(hours, agentCode || undefined).then(result => {
            if (cancelled) return;
            setLoading(false);
            setStats(result);
            setStatsError(result == null ? aiLoadErrorText(AI_LABELS.toolStats, 'Failed to load the tool statistics.') : null);
        });
        return () => { cancelled = true; };
    }, [hours, agentCode, reloadKey]);

    React.useEffect(() => { setPaging(p => ({ ...p, page: 0 })); }, [hours, agentCode, selectedTool, statusFilter]);

    React.useEffect(() => {
        let cancelled = false;
        setRowsLoading(true);
        const [status, errorType] = statusFilter.startsWith('ERROR:')
            ? ['ERROR' as const, statusFilter.split(':')[1] as AiToolErrorType]
            : [statusFilter as AiToolInvocationStatus | '', '' as const];
        getAiToolInvocations({
            hours, agentCode: agentCode || undefined, toolName: selectedTool?.toolName, target: selectedTool?.target ?? undefined,
            status, errorType,
        }, paging.page * paging.pageSize, paging.pageSize).then(page => {
            if (cancelled) return;
            setRowsLoading(false);
            setRows(page?.items ?? []);
            setTotal(page?.totalItems ?? 0);
            setRowsError(page == null ? aiLoadErrorText(AI_LABELS.toolInvocations, 'Failed to load the invocations.') : null);
        });
        return () => { cancelled = true; };
    }, [hours, agentCode, selectedTool, statusFilter, paging, reloadKey]);

    const columns: GridColDef<AiToolInvocationDto>[] = [
        { field: 'createdAt', headerName: 'Time', width: 170, valueFormatter: p => formatAiDate(p.value as string) },
        { field: 'status', headerName: 'Status', width: 120, renderCell: p => <StatusChip row={p.row} /> },
        {
            field: 'toolName', headerName: 'Tool', width: 220,
            renderCell: p => (
                <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.row.toolName}</Typography>
                    {p.row.target && <Typography variant="caption" color="text.secondary">{p.row.target}</Typography>}
                </Box>
            ),
        },
        {
            field: 'errorMessage', headerName: 'Error message', flex: 1, minWidth: 280, sortable: false,
            renderCell: p => p.row.errorMessage ? (
                <Tooltip title={<Box sx={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto' }}>{p.row.errorMessage}</Box>}>
                    <Typography variant="body2" color="error" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {p.row.errorMessage}
                    </Typography>
                </Tooltip>
            ) : <Typography variant="body2" color="text.secondary">—</Typography>,
        },
        { field: 'latencyMs', headerName: 'Latency', width: 100, valueFormatter: p => formatAiMs(p.value as number) },
        {
            field: 'agentCode', headerName: 'Agent', width: 190,
            renderCell: p => (
                <Box>
                    <Typography variant="body2">{p.row.agentCode}</Typography>
                    {p.row.rootAgentCode !== p.row.agentCode && <Typography variant="caption" color="text.secondary">in {p.row.rootAgentCode}</Typography>}
                </Box>
            ),
        },
        { field: 'endUserId', headerName: 'User', width: 90, valueFormatter: p => p.value ?? '—' },
        {
            field: 'runId', headerName: 'Run', width: 110, sortable: false,
            renderCell: p => <MuiLink component={RouterLink} to={`/ai/runs/${p.row.runId}`} sx={{ fontFamily: 'monospace' }}>{p.row.runId.slice(0, 8)}</MuiLink>,
        },
    ];

    const series = stats?.series ?? [];
    const bucketLabel = (start: string) => {
        const d = new Date(start);
        return stats?.bucket === 'HOUR'
            ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit' })
            : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    };

    return (
        <Stack gap={2} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="h4">AI tool calls</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Every tool call of the agents (MCP tools, task tools, subagents): success and failure rates, latency and error
                        messages. Kept {stats?.retentionDays ?? 30} days.
                    </Typography>
                </Box>
                <Stack direction="row" gap={1.5} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="tool-agent-label" shrink>Agent</InputLabel>
                        <Select labelId="tool-agent-label" label="Agent" value={agentCode} displayEmpty notched
                                onChange={e => { setAgentCode(e.target.value); setSelectedTool(null); }}>
                            <MenuItem value="">All agents</MenuItem>
                            {agents.map(a => <MenuItem key={a.code} value={a.code}>{a.code}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <ToggleButtonGroup size="small" exclusive value={period} onChange={(_, v) => v != null && setPeriod(v)}>
                        {PERIODS.map((p, i) => <ToggleButton key={p.label} value={i}>{p.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                    <Button startIcon={<RefreshIcon />} onClick={() => setReloadKey(k => k + 1)}>Refresh</Button>
                </Stack>
            </Stack>

            {statsError && <Alert severity="error">{statsError}</Alert>}
            {loading && !stats ? <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box> : stats && (
                <>
                    <Grid container spacing={2}>
                        <Grid item xs={6} md={2}><Metric label="Calls" value={formatAiCount(stats.calls)} /></Grid>
                        <Grid item xs={6} md={2}><Metric label="Succeeded" value={formatAiCount(stats.succeeded)} color="success.main" /></Grid>
                        <Grid item xs={6} md={2}><Metric label="Failed" value={formatAiCount(stats.failed)} color={stats.failed > 0 ? 'error.main' : undefined} /></Grid>
                        <Grid item xs={6} md={2}><Metric label="Failure rate" value={formatAiRate(stats.failureRate)} /></Grid>
                        <Grid item xs={6} md={2}>
                            <Metric label="Tool errors / call failures" value={`${formatAiCount(stats.toolErrors)} / ${formatAiCount(stats.callErrors)}`}
                                    hint="Tool errors: the tool answered with an error (a refusal, not found…). Call failures: it could not be called (unreachable, unknown tool, call limit, missing bound value)." />
                        </Grid>
                        <Grid item xs={6} md={2}><Metric label="Avg latency" value={formatAiMs(stats.avgLatencyMs)} /></Grid>
                    </Grid>

                    {series.length > 0 && (
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle1">Calls per {stats.bucket === 'HOUR' ? 'hour' : 'day'}</Typography>
                                <BarChart
                                    height={220}
                                    xAxis={[{ scaleType: 'band', data: series.map(b => bucketLabel(b.start)) }]}
                                    series={[
                                        { data: series.map(b => b.calls - b.failed), label: 'Succeeded', stack: 'calls', color: '#66bb6a' },
                                        { data: series.map(b => b.failed), label: 'Failed', stack: 'calls', color: '#ef5350' },
                                    ]}
                                    margin={{ top: 40, bottom: 30, left: 50, right: 20 }}
                                />
                            </CardContent>
                        </Card>
                    )}

                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Per tool <Typography component="span" variant="caption" color="text.secondary">— click a row to list its calls</Typography></Typography>
                            {stats.tools.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No tool calls in this period.</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Tool</TableCell>
                                            <TableCell align="right">Calls</TableCell>
                                            <TableCell align="right">Failed</TableCell>
                                            <TableCell>Failure rate</TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Avg / max latency</TableCell>
                                            <TableCell>Last error</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {stats.tools.map(t => {
                                            const selected = selectedTool?.toolName === t.toolName && selectedTool?.target === t.target;
                                            return (
                                                <TableRow key={`${t.toolName}|${t.target}`} hover selected={selected} sx={{ cursor: 'pointer' }}
                                                          onClick={() => setSelectedTool(selected ? null : t)}>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{t.toolName}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{[t.kind, t.target].filter(Boolean).join(' · ')}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right">{formatAiCount(t.calls)}</TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title={`Tool errors ${t.toolErrors} · call failures ${t.callErrors}`}>
                                                            <span>{formatAiCount(t.failed)}</span>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell><RateBar rate={t.failureRate} /></TableCell>
                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatAiMs(t.avgLatencyMs)} / {formatAiMs(t.maxLatencyMs)}</TableCell>
                                                    <TableCell sx={{ maxWidth: 360 }}>
                                                        {t.lastError ? (
                                                            <Tooltip title={<Box sx={{ whiteSpace: 'pre-wrap' }}>{t.lastError}</Box>}>
                                                                <Box>
                                                                    <Typography variant="body2" color="error" noWrap>{t.lastError}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">{formatAiDate(t.lastErrorAt)}</Typography>
                                                                </Box>
                                                            </Tooltip>
                                                        ) : '—'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                <Stack direction="row" gap={1} alignItems="center">
                    <Typography variant="h6">Invocations</Typography>
                    {selectedTool && (
                        <Chip label={`${selectedTool.toolName}${selectedTool.target ? ` · ${selectedTool.target}` : ''}`} onDelete={() => setSelectedTool(null)} />
                    )}
                </Stack>
                <ToggleButtonGroup size="small" exclusive value={statusFilter} onChange={(_, v) => v != null && setStatusFilter(v)}>
                    <ToggleButton value="">All</ToggleButton>
                    <ToggleButton value="SUCCESS">Success</ToggleButton>
                    <ToggleButton value="ERROR">Failure</ToggleButton>
                    <ToggleButton value="ERROR:TOOL">Tool errors</ToggleButton>
                    <ToggleButton value="ERROR:CALL">Call failures</ToggleButton>
                </ToggleButtonGroup>
            </Stack>
            {rowsError && <Alert severity="error">{rowsError}</Alert>}
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                getRowId={r => r.id}
                rowCount={total}
                loading={rowsLoading}
                paginationMode="server"
                paginationModel={paging}
                onPaginationModelChange={setPaging}
                pageSizeOptions={[25, 50, 100]}
                getRowHeight={() => 'auto'}
                disableRowSelectionOnClick
                sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
            />
        </Stack>
    );
}
